import React, { useCallback, useContext, useState } from "react";
import { DirectorySort } from "@common/constants/counterparty";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { saveBlobResponse } from "@lib/fileDownload";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { downloadCounterparties } from "@api/counterparty.api";
import { DownloadIcon } from "@icons";
import { DirectoryContext } from "./Directory.context";

export const DirectoryDownload: React.FC = () => {
    const activeWorkspace = useActiveWorkspace();
    const [downloading, setDownloading] = useState(false);
    const { filters, view } = useContext(DirectoryContext);
    const toaster = useToaster();

    const download = useCallback(async () => {
        if (!activeWorkspace) {
            return;
        }

        try {
            const response = await downloadCounterparties(activeWorkspace.id, {
                sort: DirectorySort.NAME_ASC,
                includeUsageStats: true,
                search: filters.search,
                startDate: filters.startDate,
                endDate: filters.endDate,
                entityIds: filters.entityIds,
                basis: filters.basis,
                coaAccountIds: view.filters.coaAccounts,
                financialAccountIds: view.filters.accountIds,
                minAmount: view.filters.minAmount,
                maxAmount: view.filters.maxAmount,
                amountDirection: view.filters.direction,
                amountRounded: view.filters.amountRounded,
                amountRoundingLevel: view.filters.amountRoundingLevel,
                isEligible1099: view.filters.isEligible1099,
                shouldExcludeCreditCardJournalEntries:
                    view.filters.shouldExcludeCreditCardJournalEntries,
                shouldExcludePaymentProcessorJournalEntries:
                    view.filters.shouldExcludePaymentProcessorJournalEntries,
                amountFilterLevel: view.filters.amountFilterLevel,
                createdByKick: view.filters.createdByKick,
                createdByUserIds: view.filters.createdByUserIds,
                createdBySources: view.filters.createdBySources,
                counterpartyReviewedFields:
                    view.filters.counterpartyReviewedFields,
                counterpartyReviewedFieldsMode:
                    view.filters.counterpartyReviewedFieldsMode,
                counterpartyUnreviewedFields:
                    view.filters.counterpartyUnreviewedFields,
                counterpartyUnreviewedFieldsMode:
                    view.filters.counterpartyUnreviewedFieldsMode,
                counterpartyReviewerIds: view.filters.counterpartyReviewerIds,
                counterpartyReviewerMode: view.filters.counterpartyReviewerMode,
                disabledFilter: view.filters.disabledFilter,
            });

            saveBlobResponse(response, "counterparties.csv");
        } catch {
            toaster.error("Failed to download file");
        }
    }, [
        activeWorkspace,
        filters.search,
        filters.startDate,
        filters.endDate,
        filters.entityIds,
        filters.basis,
        view.filters.coaAccounts,
        view.filters.accountIds,
        view.filters.minAmount,
        view.filters.maxAmount,
        view.filters.direction,
        view.filters.amountRounded,
        view.filters.amountRoundingLevel,
        view.filters.isEligible1099,
        view.filters.shouldExcludeCreditCardJournalEntries,
        view.filters.shouldExcludePaymentProcessorJournalEntries,
        view.filters.amountFilterLevel,
        view.filters.createdByKick,
        view.filters.createdByUserIds,
        view.filters.createdBySources,
        view.filters.counterpartyReviewedFields,
        view.filters.counterpartyReviewedFieldsMode,
        view.filters.counterpartyUnreviewedFields,
        view.filters.counterpartyUnreviewedFieldsMode,
        view.filters.counterpartyReviewerIds,
        view.filters.counterpartyReviewerMode,
        view.filters.disabledFilter,
        toaster,
    ]);

    return (
        <ButtonWithLoader
            variant="primary"
            size="md"
            iconOnly
            aria-label="Download counterparties"
            loading={downloading}
            setLoading={setDownloading}
            onClick={download}
        >
            <DownloadIcon />
        </ButtonWithLoader>
    );
};
